import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, ChevronLeft, Check, Eye, Plus, BarChart2, DollarSign, Receipt, Clock, CheckCircle, XCircle, History, FileText, Upload, Download, FileSpreadsheet, Edit, Trash2, Info, Calendar, TrendingUp, ChevronRight, Search, Users as Users2 } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Av, Tbl, Stat, Sec, Pr, SearchSelect, Pagination, usePagination } from "../components";
import { VendorModal } from "../components/VendorModal";
import { VendorsManager } from "../components/VendorsManager";
import { OfficeExpenses } from "../components/OfficeExpenses";
import { notify, confirmDialog } from "../notify";
import { fmtDMY, fmtDDMMYY } from "../dateFormat";

export const ExpensesPage = ({ exps = [], expTeam = [], expCats = [], role, myProfile, hashQuery, onRefresh, can = () => true }) => {
  const [expTab, setExpTab] = useState("my");
  const [expDet, setExpDet] = useState(null);
  const [expApply, setExpApply] = useState(false);
  const [expReport, setExpReport] = useState(false);
  const [expComment, setExpComment] = useState("");
  const [expPayModal, setExpPayModal] = useState(false);
  const [expSelIds, setExpSelIds] = useState([]);
  const [expPayRemarks, setExpPayRemarks] = useState("");
  const [payRuns, setPayRuns] = useState([]);
  const [payCompany, setPayCompany] = useState("");
  const [payQueue, setPayQueue] = useState([]);
  const [completedList, setCompletedList] = useState([]);
  const [cFilters, setCFilters] = useState({ from: "", to: "", company: "all", office: "all" });
  const [refCompanies, setRefCompanies] = useState([]);
  const [refOffices, setRefOffices] = useState([]);
  // ── Vendor bills (Direct Expenses) ──
  const [vendors, setVendors] = useState([]);
  const [vendorsAll, setVendorsAll] = useState(null); // active + inactive, for the Vendors tab's KPI cards only
  const [vendorCats, setVendorCats] = useState([]);
  const [bills, setBills] = useState([]);
  const [billsCompany, setBillsCompany] = useState("all");
  const [billsOffice, setBillsOffice] = useState("all");
  const [billModal, setBillModal] = useState(null); // null | {} (new) | bill obj (edit)
  const [genRecurringOpen, setGenRecurringOpen] = useState(false);
  const [ageingOpen, setAgeingOpen] = useState(false);
  const [billDet, setBillDet] = useState(null); // {id} → open BillDetailModal
  const [billsStatus, setBillsStatus] = useState('all'); // all | Pending Approval | Approved | Paid | Rejected
  const [billReject, setBillReject] = useState(null);    // {id, vendor} → open reject-with-reason modal
  const [myStatus, setMyStatus] = useState('all');       // client-side filter on My Reimbursements
  const [myCat, setMyCat] = useState('all');              // client-side category filter on My Reimbursements
  const [mySearch, setMySearch] = useState('');           // client-side search on My Reimbursements
  const [teamStatus, setTeamStatus] = useState('all');   // client-side filter on Reimbursement Approvals
  const [approvingId, setApprovingId] = useState(null);  // expense id currently being approved/rejected
  const [paying, setPaying] = useState(false);           // bulk-pay in flight
  const [cancelling, setCancelling] = useState(false);    // expense cancel in flight
  const [commenting, setCommenting] = useState(false);    // expense comment in flight
  const [billActionId, setBillActionId] = useState(null); // vendor bill id currently being approved/cancelled
  // Center Heads (and admin) approve/reject Pending Approval bills.
  const canApproveBills = can('expenses', 'bills', 'approve_bill', 'approve');
  const canRejectBills = can('expenses', 'bills', 'reject_bill', 'reject');
  // Reimbursement lifecycle statuses used across My + Approvals tabs.
  const REIMB_STATUSES = ['Pending', 'Approved', 'Rejected', 'Paid', 'Cancelled'];

  const refreshExp = () => { onRefresh(); };

  const loadPayRuns = async () => { try { const d = await api.get('/api/expenses/payment-runs'); setPayRuns(d || []); } catch (e) { /* noop */ } };
  const loadPayQueue = async (cid) => { try { const d = await api.get('/api/expenses/pay-queue' + (cid ? '?company=' + cid : '')); setPayQueue(d || []); } catch (e) { /* noop */ } };
  const loadVendors = async (active = true) => { try { const d = await api.get('/api/vendors' + (active ? '?active_only=1' : '')); const list = d || []; setVendors(list); return list; } catch (e) { return []; } };
  const loadVendorCats = async () => { try { const d = await api.get('/api/vendors/categories'); setVendorCats(d || []); } catch (e) { /* noop */ } };
  const loadBills = async (cid, status = billsStatus, office = billsOffice) => {
    const qs = new URLSearchParams();
    if (cid && cid !== 'all') qs.set('company', cid);
    if (status && status !== 'all') qs.set('status', status);
    if (office && office !== 'all') qs.set('office', office);
    try { const d = await api.get('/api/vendor-bills' + (qs.toString() ? '?' + qs.toString() : '')); setBills(d || []); } catch (e) { /* noop */ }
  };
  const loadCompleted = async (f = cFilters) => {
    const qs = new URLSearchParams();
    if (f.from) qs.set('from', f.from);
    if (f.to) qs.set('to', f.to);
    if (f.company && f.company !== 'all') qs.set('company', f.company);
    if (f.office && f.office !== 'all') qs.set('office', f.office);
    try { const d = await api.get('/api/expenses/completed' + (qs.toString() ? '?' + qs.toString() : '')); setCompletedList(d || []); } catch (e) { /* noop */ }
  };
  // Authenticated binary download (Excel bank file).
  const downloadFile = async (path, name) => {
    try {
      const r = await fetch(`${BASE}${path}`, { headers: { Authorization: 'Bearer ' + api.getToken() } });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    } catch (e) { notify(e.message || 'Download failed'); }
  };

  // Loaded once up-front (for admin/accounts only — same gate as the
  // backend route) so the "My Reimbursements" info deck can show the most
  // recent disbursement run without waiting for the Bank Files tab.
  React.useEffect(() => { if (can('expenses', 'vendors', null, 'view')) loadPayRuns(); }, []); // eslint-disable-line

  React.useEffect(() => {
    if (['payment', 'completed', 'bills', 'vendors'].includes(expTab)) {
      if (!refCompanies.length) api.get('/api/companies?active=true').then(d => { const c = Array.isArray(d) ? d : d.data || []; setRefCompanies(c); if (expTab === 'payment' && c.length && !payCompany) setPayCompany(String(c[0].id)); }).catch(() => {});
      else if (expTab === 'payment' && !payCompany) setPayCompany(String(refCompanies[0].id));
    }
    if (expTab === 'bankfiles') loadPayRuns();
    if (expTab === 'completed') loadCompleted();
    // Bills need them too — the Add Vendor Bill modal's Centre picker was
    // empty because offices were only ever loaded on the Completed tab.
    if (['completed', 'bills'].includes(expTab) && !refOffices.length) {
      api.get('/api/offices').then(d => setRefOffices(Array.isArray(d) ? d : d.data || [])).catch(() => {});
    }
    if (expTab === 'bills') {
      loadVendors(true);
      if (!vendorCats.length) loadVendorCats();
      loadBills(billsCompany);
    }
    if (expTab === 'vendors') {
      api.get('/api/vendors').then(d => setVendorsAll(Array.isArray(d) ? d : [])).catch(() => setVendorsAll([]));
    }
  }, [expTab]); // eslint-disable-line

  React.useEffect(() => {
    if (expTab === 'payment' && payCompany) { loadPayQueue(payCompany); setExpSelIds([]); }
  }, [expTab, payCompany]); // eslint-disable-line

  React.useEffect(() => {
    if (expTab === 'bills') loadBills(billsCompany);
  }, [billsCompany, billsStatus, billsOffice]); // eslint-disable-line

  const approveExp = async (eid, action, cmt) => {
    setApprovingId(eid);
    try {
      if (action === 'Rejected') await api.put(`/api/expenses/${eid}/reject`, { reason: cmt });
      else await api.put(`/api/expenses/${eid}/approve`, { comment: cmt });
      refreshExp();
      notify(action === 'Rejected' ? 'Expense rejected' : 'Expense approved', { title: action === 'Rejected' ? 'Rejected' : 'Approved', variant: 'success' });
    } catch (e) { notify(e.message || "Action failed", { title: "Error", variant: "error" }); }
    finally { setApprovingId(null); }
  };
  const toggleSel = (eid) => { setExpSelIds(prev => prev.includes(eid) ? prev.filter(x => x !== eid) : [...prev, eid]); };
  const toggleAllSel = () => { setExpSelIds(prev => prev.length === payQueue.length ? [] : payQueue.map(e => e.id)); };
  const doBulkPay = async () => {
    if (expSelIds.length === 0) return;
    setPaying(true);
    try {
      // Split selection by row type so backend can update both tables.
      const sel = payQueue.filter(x => expSelIds.includes(x.id));
      const expense_ids = sel.filter(x => x.type === 'reimbursement').map(x => x.id);
      const bill_ids = sel.filter(x => x.type === 'vendor').map(x => x.id);
      const res = await api.post('/api/expenses/bulk-pay', { expense_ids, bill_ids, remarks: expPayRemarks });
      if (res?.payRef) await downloadFile('/api/expenses/bank-file/' + res.payRef, `bankfile-${res.payRef}.xlsx`);
      setExpPayModal(false); setExpSelIds([]); setExpPayRemarks("");
      refreshExp(); loadPayRuns(); if (payCompany) loadPayQueue(payCompany);
      notify(`Paid \u2014 ${res?.paidExp || 0} reimbursement(s), ${res?.paidBills || 0} vendor bill(s)${res?.skipped ? `, ${res.skipped} skipped` : ''}. Bank file downloaded.`, { title: "Paid", variant: "success" });
    } catch (e) { notify(e.message || "Payment failed", { title: "Error", variant: "error" }); }
    finally { setPaying(false); }
  };
  const openExpDet = async (eid) => { try { const d = await api.get('/api/expenses/' + eid); setExpDet(d); setExpComment(""); } catch (e) { console.error(e); } };
  // Deep link from a notification. Two distinct shapes share this page:
  //   - Reimbursement claim: #expenses?view=pending&id=<id> opens that
  //     claim directly and, for a manager's "needs your approval"
  //     notification, lands on the Approvals tab.
  //   - Vendor bill: #expenses?tab=vendor-bills&id=<id> (server/routes/
  //     vendorBills.js) — a different entity/endpoint entirely, so it opens
  //     BillDetailModal on the Bills tab instead of treating id as a claim.
  useEffect(() => {
    if (!hashQuery?.id) return;
    if (hashQuery.tab === 'vendor-bills') {
      setExpTab('bills');
      setBillDet({ id: hashQuery.id });
      return;
    }
    openExpDet(hashQuery.id);
    if (hashQuery.view === 'pending') setExpTab('approvals');
  }, [hashQuery?.id]); // eslint-disable-line
  const cancelExp = async (eid) => {
    setCancelling(true);
    try {
      await api.put('/api/expenses/' + eid + '/cancel');
      setExpDet(null); refreshExp();
      notify('Expense claim cancelled', { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Failed to cancel expense', { title: 'Error', variant: 'error' }); }
    finally { setCancelling(false); }
  };
  const addExpComment = async (eid) => {
    if (!expComment.trim()) return;
    setCommenting(true);
    try {
      await api.post('/api/expenses/' + eid + '/comment', { comment: expComment });
      setExpComment(""); openExpDet(eid);
      notify('Comment added', { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Failed to add comment', { title: 'Error', variant: 'error' }); }
    finally { setCommenting(false); }
  };
  const stClrE = (s) => s === "Paid" ? K.g : (s || "").includes("OK") ? K.bl : s === "Rejected" ? K.r : s === "Cancelled" ? K.t3 : K.a;
  // Vendor bill status colors — Pending Approval = amber (needs action),
  // Approved = blue (payable), Paid = green (done), Rejected = red.
  const billStatusColor = (s) =>
    s === 'Paid' ? K.g :
    s === 'Approved' ? K.bl :
    s === 'Pending Approval' ? K.a :
    s === 'Rejected' ? K.r :
    s === 'Cancelled' ? K.t3 : K.t3;
  const fmtAmt = (a) => `₹${(a || 0).toLocaleString("en-IN")}`;
  const allExp = [...exps, ...expTeam].filter((v, i, a) => a.findIndex(x => x.id === v.id) === i);
  const pendingClaims = allExp.filter(e => e.status === "Pending");
  const approvedClaims = allExp.filter(e => e.status === "Approved");
  const paidClaims = allExp.filter(e => e.status === "Paid");
  const pendingAmt = pendingClaims.reduce((s, e) => s + (e.amt || 0), 0);
  const approvedAmt = approvedClaims.reduce((s, e) => s + (e.amt || 0), 0);
  const paidAmt = paidClaims.reduce((s, e) => s + (e.amt || 0), 0);

  // Pagination state lives at the top level (not inside the conditional tab
  // blocks below) so these hooks run unconditionally on every render,
  // regardless of which tab is active.
  const mySearchLC = mySearch.trim().toLowerCase();
  const filteredMy = exps.filter(e =>
    (myStatus === 'all' || e.status === myStatus) &&
    (myCat === 'all' || e.cat === myCat) &&
    (!mySearchLC || (e.desc || '').toLowerCase().includes(mySearchLC) || String(e.id).includes(mySearchLC) || (e.catNm || '').toLowerCase().includes(mySearchLC))
  );
  const myPage = usePagination(filteredMy, 5);
  React.useEffect(() => { myPage.onPageChange(1); }, [myStatus, myCat, mySearch]); // eslint-disable-line
  const approvalsPage = usePagination(expTeam, 10);
  const billsPage = usePagination(bills, 10);
  const completedPage = usePagination(completedList, 10);

  // Real, already-loaded data for the "My Reimbursements" info deck — no
  // fabricated figures. Cheapest category limit with a receipt rule (a
  // genuine policy fact), the most recent actual disbursement run (if this
  // role can see payRuns), and this month's top-spending category.
  const cheapestLimitCat = expCats.filter(c => c.active !== false && c.mx > 0).sort((a, b) => a.mx - b.mx)[0];
  const lastRun = payRuns[0];
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthCatSpend = {};
  allExp.filter(e => (e.expDate || '').slice(0, 7) === thisMonth).forEach(e => {
    const c = e.catNm || e.cat || 'Other';
    monthCatSpend[c] = (monthCatSpend[c] || 0) + (e.amt || 0);
  });
  const topCatEntry = Object.entries(monthCatSpend).sort((a, b) => b[1] - a[1])[0];

  const SubmitExpModalImpl = () => {
    const activeCats = expCats.filter(c => c.active !== false);
    const [af, setAf] = useState({ cat: activeCats[0]?.cd || "", amount: "", desc: "", date: new Date().toISOString().slice(0, 10) });
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
    const selCat = activeCats.find(c => c.cd === af.cat);
    const amt = parseFloat(af.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const doSubmit = async () => {
      if (!af.amount || amt <= 0) return setErr("Enter a valid amount");
      if (!af.desc.trim()) return setErr("Enter a description");
      if (af.date > today) return setErr("Expense date cannot be in the future");
      if (selCat && selCat.mx > 0 && amt > selCat.mx) return setErr(`Amount exceeds category limit of ${fmtAmt(selCat.mx)}`);
      if (files.length === 0) return setErr("A bill / receipt is required.");
      setSaving(true); setErr("");
      try {
        const fd = new FormData();
        fd.append("category_code", af.cat);
        fd.append("amount", String(amt));
        fd.append("description", af.desc);
        fd.append("expense_date", af.date);
        files.forEach(f => fd.append("receipt", f));
        await api.upload("/api/expenses", fd);
        setExpApply(false); refreshExp();
        notify("Expense claim submitted", { title: "Submitted", variant: "success" });
      } catch (e) { setErr(e.message || "Error"); notify(e.message || "Failed to submit expense", { title: "Error", variant: "error" }); } finally { setSaving(false); }
    };
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" >
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[991px] max-h-[85vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><span className="text-[18px] font-extrabold text-t0">Submit Expense</span><button onClick={() => setExpApply(false)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
        <div className="grid gap-4 grid-cols-1 md:[grid-template-columns:1fr_220px]">
          <div>
            <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Category <span className="text-r">*</span></div><Sl options={activeCats.map(c => ({ v: c.cd, l: c.nm }))} value={af.cat} onChange={v => setAf({ ...af, cat: v })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-3">
              <div><div className="text-[14px] text-t2 mb-[3px]">Amount (₹) <span className="text-r">*</span></div><Inp type="number" placeholder="5000" value={af.amount} onChange={v => setAf({ ...af, amount: v })} /></div>
              <div><div className="text-[14px] text-t2 mb-[3px]">Expense Date</div><Inp type="date" value={af.date} onChange={v => setAf({ ...af, date: v })} /></div>
            </div>
            <div className="mb-3"><div className="flex justify-between text-[14px] text-t2 mb-[3px]"><span>Description <span className="text-r">*</span></span><span className={`text-[12px] ${af.desc.length > 200 ? "text-r" : "text-t3"}`}>{af.desc.length}/200</span></div><textarea value={af.desc} maxLength={200} onChange={e => setAf({ ...af, desc: e.target.value })} placeholder="Describe the expense..." className="w-full min-h-[60px] p-[10px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y" /></div>
            <div className="mb-3">
              <div className="text-[14px] text-t2 mb-[3px]">Bill / Receipt <span className="text-r">*</span></div>
              {files.length > 0 && <div className="mb-2 flex flex-col gap-1">
                {files.map((f, i) => <div key={f.name + i} className="flex items-center gap-2 px-3 py-[6px] bg-bg2 border border-b0 rounded-[10px]">
                  <FileText size={13} className="text-t3 flex-shrink-0" />
                  <span className="text-[14px] text-t1 truncate flex-1">{f.name}</span>
                  <button type="button" aria-label={"Remove " + f.name} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="bg-transparent border-0 text-t3 cursor-pointer p-0"><X size={14} /></button>
                </div>)}
              </div>}
              {files.length < 10 && <label className="flex items-center gap-2 px-3 py-2 bg-bg2 border border-dashed border-b1 rounded-[10px] cursor-pointer hover:border-p">
                <Upload size={13} className="text-t3 flex-shrink-0" />
                <span className="text-[14px] text-t2 truncate">{files.length ? "Add more photos / bills" : "Attach PDF or images (jpg, png) — you can select several"}</span>
                <input type="file" multiple accept=".pdf,image/*" className="hidden" onChange={e => { const picked = Array.from(e.target.files || []); e.target.value = ""; setFiles(prev => [...prev, ...picked].slice(0, 10)); }} />
              </label>}
              {files.length === 0 && <div className="text-[12px] text-a mt-1">A bill is mandatory for every claim.</div>}
              {files.length >= 10 && <div className="text-[12px] text-t3 mt-1">Maximum 10 files per claim.</div>}
            </div>
            {err && <div className="text-[14px] text-r mb-2">{err}</div>}
            <div className="flex gap-[6px]"><Bt full onClick={doSubmit} disabled={saving}>{saving ? "Submitting..." : "Submit Expense"}</Bt><Bt v="secondary" full onClick={() => setExpApply(false)}>Cancel</Bt></div>
          </div>
          <div><Cd style={{ position: "sticky", top: 0 }}>
            <div className="text-[14px] font-semibold text-t2 mb-2">Category Policy</div>
            {selCat && <div>
              <div className="flex justify-between mb-1 text-[14px]"><span className="text-t2">Max amount</span><span className="font-bold text-t0">{selCat.mx > 0 ? fmtAmt(selCat.mx) : "No limit"}</span></div>
              <div className="flex justify-between mb-1 text-[14px]"><span className="text-t2">Receipt required</span><span className="font-semibold text-t0">{selCat.rcptAbove === null ? "Not required" : selCat.rcptAbove > 0 ? `Above ${fmtAmt(selCat.rcptAbove)}` : "Always"}</span></div>
            </div>}
            <div className="border-t border-b0 mt-2 pt-2"><div className="text-[12px] text-t3 mb-1">APPROVAL</div><div className="text-[14px] text-bl font-semibold leading-[1.6]">Centre Head → Accounts</div></div>
          </Cd></div>
        </div>
      </div>
    </div>;
  };

  // This modal is defined inside the page component, so it was a brand-new component on every render. The app refreshes
  // notifications every minute, which re-renders the page, and that unmounted the modal and wiped whatever was typed.
  // One component type that never changes, always running the latest version of the form, keeps what was entered.
  const submitRef = useRef(SubmitExpModalImpl); submitRef.current = SubmitExpModalImpl;
  const SubmitExpModal = useMemo(() => function SubmitExpModal() { return submitRef.current(); }, []);

  const ExpReportModal = () => {
    const catSpend = {}; allExp.forEach(e => { const c = e.cat || "MISC"; if (!catSpend[c]) catSpend[c] = { amt: 0, cnt: 0 }; catSpend[c].amt += (e.amt || 0); catSpend[c].cnt++; });
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => setExpReport(false)}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[824px] max-h-[85vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Expense Report by Category</span><button onClick={() => setExpReport(false)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
        <div className="grid gap-2">
          {expCats.map(c => { const sp = catSpend[c.cd] || { amt: 0, cnt: 0 }; const pct = c.mx > 0 ? Math.min(sp.amt / c.mx * 100, 100) : 0; return <div key={c.cd} className="flex items-center bg-bg2 rounded-[10px] border border-b0 overflow-hidden">
            <div className="w-1 h-[60px] flex-shrink-0" style={{ background: c.clr }} />
            <div className="flex-1 px-3 py-2">
              <div className="flex justify-between mb-1"><span className="text-[14px] font-bold text-t0">{c.nm}</span><span className="text-[14px] font-bold text-t0">{fmtAmt(sp.amt)}</span></div>
              <Pr value={pct} c={pct > 90 ? K.r : pct > 70 ? K.a : c.clr} h={4} />
              <div className="flex justify-between mt-[3px]"><span className="text-[12px] text-t3">{sp.cnt} claims</span><span className="text-[12px] text-t3">Limit: {c.mx > 0 ? fmtAmt(c.mx) : "None"}</span></div>
            </div>
          </div>; })}
        </div>
        <Bt v="secondary" full style={{ marginTop: 12 }} onClick={() => setExpReport(false)}>Close</Bt>
      </div>
    </div>;
  };

  if (expDet) {
    const det = expDet;
    const isPending = det.status === "Pending";
    const canApprove = can('expenses', 'approvals', 'approve_reimbursement', 'approve') && det.status === 'Pending';
    const canReject = can('expenses', 'approvals', 'reject_reimbursement', 'reject') && det.status === 'Pending';
    return <div>
      <Bt v="ghost" s="sm" icon={ChevronLeft} onClick={() => setExpDet(null)} style={{ marginBottom: 10 }}>Back to Expenses</Bt>
      <div className="grid gap-3 grid-cols-1 lg:[grid-template-columns:1fr_220px]">
        <div>
          <Cd style={{ marginBottom: 12 }}>
            <div className="flex items-center justify-between mb-[14px] flex-wrap gap-2">
              <div className="flex items-center gap-[10px]"><Av nm={det.enm} sz={36} /><div><div className="text-[15px] font-bold text-t0">{det.eid} - {det.enm}</div><div className="text-[14px] text-t3">{det.erl} · {det.eofc}</div></div></div>
              <Bg c={stClrE(det.status)} dot>{det.status}</Bg>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-[14px]">
              <div><div className="text-[12px] text-t3 mb-[2px]">Category</div><Bg c={det.catClr}>{det.catNm}</Bg></div>
              <div><div className="text-[12px] text-t3 mb-[2px]">Amount</div><span className="text-[17px] font-extrabold text-t0">{fmtAmt(det.amt)}</span></div>
              <div><div className="text-[12px] text-t3 mb-[2px]">Expense Date</div><span className="text-[14px] text-t1">{det.expDate || "—"}</span></div>
            </div>
            <div className="mb-3"><div className="text-[12px] text-t3 mb-[2px]">Description</div><span className="text-[14px] text-t1">{det.desc}</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div><div className="text-[12px] text-t3 mb-[2px]">Receipt</div>
                {(det.receipts && det.receipts.length > 0) || det.rcptUrl
                  ? <div className="flex flex-col gap-[2px]">{(det.receipts && det.receipts.length ? det.receipts : [{ url: det.rcptUrl }]).map((r, i, arr) =>
                      <a key={r.url} href={uploadUrl(r.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[14px] font-semibold text-bl hover:underline"><FileText size={11} /> {arr.length > 1 ? `Receipt ${i + 1}${r.name ? " — " + r.name : ""}` : "View receipt"}</a>)}</div>
                  : det.rcpt ? <Bg c={K.a} sm>Declared, no file</Bg> : <Bg c={K.r} sm>Missing</Bg>}
              </div>
              <div><div className="text-[12px] text-t3 mb-[2px]">Date of request</div><span className="text-[14px] text-t1">{det.createdAt ? fmtDMY(det.createdAt) : "—"}</span></div>
            </div>
            {det.payRef && <div className="mb-3"><div className="text-[12px] text-t3 mb-[2px]">Payment Reference</div><span className="text-[14px] text-g font-semibold">{det.payRef}</span></div>}
            {det.rejReason && <div className="mb-3 p-2 bg-rb rounded-lg border border-rd"><div className="text-[12px] text-r mb-[2px]">Rejection Reason</div><span className="text-[14px] text-t0">{det.rejReason}</span></div>}
            <div className="flex gap-[6px] flex-wrap">
              {isPending && <Bt v="danger" s="sm" icon={X} onClick={() => cancelExp(det.id)} disabled={cancelling}>{cancelling ? "Cancelling…" : "Cancel"}</Bt>}
              {canApprove && <Bt v="success" s="sm" icon={Check} onClick={() => { approveExp(det.id, "approve"); setExpDet(null); }}>Approve</Bt>}
              {canReject && <Bt v="danger" s="sm" icon={X} onClick={() => { approveExp(det.id, "Rejected"); setExpDet(null); }}>Reject</Bt>}
            </div>
          </Cd>
          <Cd>
            <Sec icon={History} title="Approval Timeline" />
            <div className="relative pl-5">
              <div className="absolute left-[7px] top-0 bottom-0 w-[2px] bg-b1" />
              {(det.timeline || []).map((t, i) => {
                const clr = t.action === "approved" || t.action === "verified" ? K.g : t.action === "rejected" ? K.r : t.action === "cancelled" ? K.t3 : t.action === "paid" ? K.g : t.action === "comment" ? K.pu : K.a;
                return <div key={t.id || i} className="relative mb-[14px] pl-[14px]">
                  <div className="absolute -left-[14px] top-[2px] w-[10px] h-[10px] rounded-full" style={{ background: clr, border: `2px solid ${K.bg1}` }} />
                  <div className="text-[14px] font-semibold text-t0">{t.action === "submitted" ? "Expense submitted" : t.action === "approved" ? "Approved" : t.action === "verified" ? "Financially Verified" : t.action === "paid" ? "Payment Processed" : t.action === "rejected" ? "Rejected" : t.action === "cancelled" ? "Cancelled" : t.action === "comment" ? "Comment" : t.action}</div>
                  <div className="text-[12px] text-t3">{t.at ? `${fmtDMY(t.at)}, ${new Date(t.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}  {t.by && <span>· {t.by}</span>}</div>
                  {t.comment && <div className="mt-[3px] px-2 py-[5px] bg-bg2 rounded text-[14px] text-t1 border border-b0">{t.comment}</div>}
                </div>;
              })}
            </div>
            <div className="mt-2 flex gap-[6px]">
              <textarea value={expComment} onChange={e => setExpComment(e.target.value)} placeholder="Add a comment..." className="flex-1 min-h-[40px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-none" />
              <Bt onClick={() => addExpComment(det.id)} disabled={!expComment.trim() || commenting}>{commenting ? "Adding…" : "Comment"}</Bt>
            </div>
          </Cd>
        </div>
        <div><Cd style={{ position: "sticky", top: 0 }}>
          <div className="text-[14px] font-semibold text-t2 mb-2">Approval Flow</div>
          <div className="text-[14px] text-bl font-semibold leading-[1.8]">Employee → Centre Head → Accounts → Paid</div>
          <div className="border-t border-b0 mt-2 pt-2">
            <div className="text-[12px] text-t3 mb-[2px]">Category limit</div>
            <span className="text-[14px] font-bold text-t0">{det.catMx > 0 ? fmtAmt(det.catMx) : "No limit"}</span>
          </div>
        </Cd></div>
      </div>
    </div>;
  }

  const expTabs = ["my"];
  if (can('expenses', 'approvals', null, 'view')) expTabs.push("approvals");
  // Vendor Bills needs centre_head too — they're the approver in this
  // flow ("Accounts uploads a bill -> Centre Head approves it -> Accounts
  // pays"), same role list as Reimbursement Approvals above. The backend
  // (vendorBills.js's CH_OR_ACCOUNTS + getCentreScope office/company
  // scoping) already allowed this; only the tab itself was gated too
  // narrowly. Vendor/Payment/Bank Files/Completed stay Accounts+Admin
  // only — those are Accounts-internal admin, not something a Centre
  // Head approves.
  if (can('expenses', 'bills', null, 'view')) expTabs.push("bills");
  if (can('expenses', 'vendors', null, 'view')) expTabs.push("vendors");
  if (can('expenses', 'payment', null, 'view')) expTabs.push("payment");
  if (can('expenses', 'bankfiles', null, 'view')) expTabs.push("bankfiles");
  if (can('expenses', 'completed', null, 'view')) expTabs.push("completed");
  if (can('expenses', 'ledger', null, 'view')) expTabs.push("ledger");
  const expTabLabel = (t) =>
    t === "my" ? "My Reimbursements"
    : t === "approvals" ? "Reimbursement Approvals"
    : t === "bills" ? "Vendor Bills"
    : t === "vendors" ? "Vendors"
    : t === "payment" ? "Payment Processing"
    : t === "bankfiles" ? "Bank Files"
    : t === "ledger" ? "Expenses"
    : "Completed Payments";
  const ExpTabBar = () => (
    <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px] mb-3 flex-wrap">
      {expTabs.map(t => (
        <button
          key={t}
          onClick={() => setExpTab(t)}
          className={`px-4 py-[7px] rounded-lg text-[13px] font-bold transition-colors flex items-center gap-[6px] cursor-pointer border-0 ${expTab === t ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}
        >
          {expTabLabel(t)}
          {t === "approvals" && expTeam.length > 0 && <span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold" style={{ background: K.pg, color: K.p }}>{expTeam.length} Pending</span>}
        </button>
      ))}
    </div>
  );

  // The 4 hero KPI cards are tab-scoped — each tab has its own real data
  // shape (a personal claim, a team-approval queue, a vendor bill, a pay-run,
  // a vendor record…) so "Total/Pending/Approved/Paid" from the My
  // Reimbursements tab would be meaningless (or just wrong) on, say, Bank
  // Files. Every number below is a genuine aggregate of that tab's own
  // already-loaded data — nothing here is invented.
  const kpiCards = (() => {
    if (expTab === "approvals") {
      const amt = expTeam.reduce((s, e) => s + (e.amt || 0), 0);
      const uniqueEmp = new Set(expTeam.map(e => e.eid || e.enm)).size;
      const oldest = expTeam.map(e => e.createdAt).filter(Boolean).sort()[0];
      const oldestDays = oldest ? Math.max(0, Math.floor((Date.now() - new Date(oldest).getTime()) / 86400000)) : null;
      return [
        { icon: Clock,       label: "Team Pending",   value: expTeam.length,     sub: expTeam.length ? `${fmtAmt(amt)} pending review` : 'Nothing awaiting review', c: K.a },
        { icon: DollarSign,  label: "Amount Pending",  value: fmtAmt(amt),       sub: `Across ${expTeam.length} claim${expTeam.length === 1 ? '' : 's'}`, c: K.p },
        { icon: Users2,      label: "Employees Waiting", value: uniqueEmp,       sub: uniqueEmp ? `${uniqueEmp} distinct employee${uniqueEmp === 1 ? '' : 's'}` : 'None waiting', c: K.bl },
        { icon: History,     label: "Oldest Pending",  value: oldestDays != null ? `${oldestDays}d` : "—", sub: oldestDays != null ? 'Days since raised' : 'No pending claims', c: K.r },
      ];
    }
    if (expTab === "bills") {
      const pend = bills.filter(b => b.status === 'Pending Approval');
      const appr = bills.filter(b => b.status === 'Approved');
      const paid = bills.filter(b => b.status === 'Paid');
      const pendAmt = pend.reduce((s, b) => s + (b.netPayable || b.total || 0), 0);
      const apprAmt = appr.reduce((s, b) => s + (b.netPayable || b.total || 0), 0);
      const paidAmt2 = paid.reduce((s, b) => s + (b.netPayable || b.total || 0), 0);
      return [
        { icon: Receipt,     label: "Total Bills", value: bills.length,   sub: `${bills.length} bill${bills.length === 1 ? '' : 's'} total`, c: K.p },
        { icon: Clock,       label: "Pending Approval", value: pend.length, sub: pend.length ? `${fmtAmt(pendAmt)} awaiting approval` : 'Nothing awaiting approval', c: K.a },
        { icon: CheckCircle, label: "Approved",    value: fmtAmt(apprAmt), sub: `${appr.length} payable`, c: K.bl },
        { icon: DollarSign,  label: "Paid",        value: fmtAmt(paidAmt2), sub: `${paid.length} settled`, c: K.g },
      ];
    }
    if (expTab === "vendors") {
      const list = vendorsAll || [];
      const active = list.filter(v => v.active !== false);
      const withBank = list.filter(v => v.bankName && v.account);
      const withTax = list.filter(v => v.pan || v.gstin);
      return [
        { icon: Users2,      label: "Total Vendors", value: vendorsAll === null ? "—" : list.length, sub: vendorsAll === null ? 'Loading…' : `${list.length} on record`, c: K.p },
        { icon: CheckCircle, label: "Active",        value: vendorsAll === null ? "—" : active.length, sub: `${list.length - active.length} inactive`, c: K.g },
        { icon: FileText,    label: "PAN / GSTIN on File", value: vendorsAll === null ? "—" : withTax.length, sub: list.length ? `${Math.round(withTax.length / list.length * 100)}% of vendors` : 'No vendors yet', c: K.bl },
        { icon: DollarSign,  label: "Bank Details on File", value: vendorsAll === null ? "—" : withBank.length, sub: list.length ? `${Math.round(withBank.length / list.length * 100)}% ready to pay` : 'No vendors yet', c: K.pu },
      ];
    }
    if (expTab === "payment") {
      const reimb = payQueue.filter(x => x.type === 'reimbursement');
      const vend = payQueue.filter(x => x.type === 'vendor');
      const netTotal = payQueue.reduce((s, x) => s + (x.net || 0), 0);
      const selNet = payQueue.filter(x => expSelIds.includes(x.id)).reduce((s, x) => s + (x.net || 0), 0);
      return [
        { icon: Clock,       label: "Ready to Pay",  value: payQueue.length, sub: `${fmtAmt(netTotal)} net payable`, c: K.a },
        { icon: Receipt,     label: "Reimbursements", value: reimb.length,  sub: `${reimb.length} in queue`, c: K.bl },
        { icon: FileText,    label: "Vendor Bills",  value: vend.length,    sub: `${vend.length} in queue`, c: K.pu },
        { icon: DollarSign,  label: "Selected",      value: expSelIds.length, sub: expSelIds.length ? `${fmtAmt(selNet)} to disburse` : 'Nothing selected', c: K.g },
      ];
    }
    if (expTab === "bankfiles") {
      const total = payRuns.reduce((s, r) => s + (r.total || 0), 0);
      const entries = payRuns.reduce((s, r) => s + (r.count || 0), 0);
      const thisMonthKey = new Date().toISOString().slice(0, 7);
      const thisMonthRuns = payRuns.filter(r => (r.paidAt || '').slice(0, 7) === thisMonthKey);
      const last = payRuns[0];
      return [
        { icon: FileText,    label: "Total Bank Files", value: payRuns.length, sub: `${entries} entries total`, c: K.p },
        { icon: DollarSign,  label: "Total Disbursed", value: fmtAmt(total),  sub: `Across ${payRuns.length} run${payRuns.length === 1 ? '' : 's'}`, c: K.g },
        { icon: Clock,       label: "This Month",     value: thisMonthRuns.length, sub: thisMonthRuns.length ? `${fmtAmt(thisMonthRuns.reduce((s, r) => s + (r.total || 0), 0))} disbursed` : 'No runs yet this month', c: K.bl },
        { icon: History,     label: "Last Run",       value: last?.paidAt ? new Date(last.paidAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—", sub: last ? `Ref ${last.ref}` : 'No runs yet', c: K.pu },
      ];
    }
    if (expTab === "completed") {
      const total = completedList.reduce((s, e) => s + (e.amt || 0), 0);
      const reimb = completedList.filter(e => e.type === 'reimbursement');
      const vend = completedList.filter(e => e.type === 'vendor');
      return [
        { icon: CheckCircle, label: "Total Payments", value: completedList.length, sub: `${completedList.length} payment${completedList.length === 1 ? '' : 's'}`, c: K.g },
        { icon: DollarSign,  label: "Total Amount",   value: fmtAmt(total),  sub: 'For the selected filters', c: K.p },
        { icon: Receipt,     label: "Reimbursements", value: reimb.length,   sub: `${reimb.length} settled`, c: K.bl },
        { icon: FileText,    label: "Vendor Bills",   value: vend.length,    sub: `${vend.length} settled`, c: K.pu },
      ];
    }
    // "my" (default)
    return [
      { icon: Receipt,     label: "Total Claims", value: allExp.length,        sub: `${allExp.length} claim${allExp.length === 1 ? '' : 's'} total`,            c: K.p },
      { icon: Clock,       label: "Pending",      value: pendingClaims.length, sub: pendingClaims.length ? `${fmtAmt(pendingAmt)} awaiting review` : 'Nothing awaiting review', c: K.a },
      { icon: CheckCircle, label: "Approved",     value: fmtAmt(approvedAmt), sub: `${approvedClaims.length} in queue`,                                        c: K.bl },
      { icon: DollarSign,  label: "Paid",         value: fmtAmt(paidAmt),     sub: `${paidClaims.length} settled`,                                             c: K.g },
    ];
  })();

  return <div>
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-[14px]">
      <div>
        <div className="flex items-center gap-1.5 text-[12px] text-t3 font-medium mb-1">
          <span>Finance</span>
          <ChevronRight size={12} className="text-t3" />
          <span className="text-p font-semibold">Expenses & Reimbursements</span>
        </div>
        <h1 className="m-0 text-2xl font-bold text-t0 tracking-tight">Expenses & Reimbursements</h1>
        <p className="text-[12px] text-t3 mt-0.5">Manage employee expense claims, vendor bills, and disbursement approvals.</p>
      </div>
      {/* Submitting a claim belongs to My Reimbursements — it was showing on
          Vendor Bills, Vendors, Payment Processing and the rest, where the
          work on screen has nothing to do with raising your own claim. */}
      <div className="flex gap-2 flex-wrap">
        <Bt v="secondary" s="sm" icon={BarChart2} onClick={() => setExpReport(true)}>Report</Bt>
        {expTab === "my" && <Bt icon={Plus} s="sm" onClick={() => setExpApply(true)}>Submit Expense</Bt>}
      </div>
    </div>
    {expTab !== "ledger" && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      {kpiCards.map((s, i) => (
        <div key={i} className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-shadow flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide truncate">{s.label}</div>
            <div className="text-[22px] font-extrabold text-t0 tracking-tight mt-[2px]">{s.value}</div>
            <div className="text-[11px] text-t3 mt-[2px] truncate">{s.sub}</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}14`, color: s.c }}>
            <s.icon size={20} />
          </div>
        </div>
      ))}
    </div>}
    <ExpTabBar />
    {expTab === "my" && <div>
      <div className="flex items-center gap-[8px] mb-3 flex-wrap">
        <div className="w-[200px]"><Inp icon={Search} placeholder="Search description, ID…" value={mySearch} onChange={setMySearch} /></div>
        <div className="w-[160px]"><Sl options={[{ v: 'all', l: 'All Categories' }, ...expCats.map(c => ({ v: c.cd, l: c.nm }))]} value={myCat} onChange={setMyCat} /></div>
        <div className="w-[150px]"><Sl options={[{ v: 'all', l: 'All statuses' }, ...REIMB_STATUSES.map(s => ({ v: s, l: s }))]} value={myStatus} onChange={setMyStatus} /></div>
        {(myStatus !== 'all' || myCat !== 'all' || mySearch) && <button onClick={() => { setMyStatus('all'); setMyCat('all'); setMySearch(''); }} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer">Clear</button>}
      </div>
      {filteredMy.length === 0
        ? <div className="text-center p-[30px] text-t3 text-[14px] bg-bg1 border border-b0 rounded-2xl">{myStatus === 'all' && myCat === 'all' && !mySearch ? 'No expense claims' : 'No claims match these filters'}</div>
        : <div className="rounded-2xl border border-b0 overflow-hidden shadow-card">
            <Tbl bare cols={[
              { k: "cat", l: "Category", r: r => <div><Bg c={r.catClr || K.p} sm>{r.catNm || r.type}</Bg><div className="text-[11px] text-t3 mt-[3px]">EXP-{r.id}</div></div> },
              { k: "date", l: "Date", r: r => <span className="text-[14px] text-t1">{r.expDate || "—"}</span> },
              { k: "amt", l: "Amount", r: r => <span className="font-bold">{fmtAmt(r.amt)}</span> },
              { k: "desc", l: "Description", r: r => <span className="text-[12px] text-t2 max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap inline-block">{r.desc}</span> },
              { k: "rcpt", l: "Receipt", r: r => r.rcptUrl
                  ? <a href={uploadUrl(r.rcptUrl)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-[12px] font-semibold text-bl hover:underline"><FileText size={11} /> View{r.rcptCount > 1 ? ` (${r.rcptCount})` : ""}</a>
                  : r.rcpt ? <Bg c={K.a} sm>No file</Bg> : <Bg c={K.r} sm>Missing</Bg> },
              { k: "st", l: "Status", r: r => <Bg c={stClrE(r.status)} dot sm>{r.status}</Bg> },
              { k: "a", l: "", r: r => <Bt v="ghost" s="xs" icon={Eye} onClick={() => openExpDet(r.id)}>View</Bt> }
            ]} data={myPage.pageData} />
            <Pagination total={filteredMy.length} page={myPage.page} pageSize={myPage.pageSize} onPageChange={myPage.onPageChange} onPageSizeChange={myPage.onPageSizeChange} />
          </div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <div className="bg-bg1 border border-b0 rounded-2xl p-4 shadow-sm flex items-start gap-3">
          <div className="p-2 bg-pg text-p rounded-xl flex-shrink-0"><Info size={18} /></div>
          <div>
            <h4 className="text-[13px] font-bold text-t0">Quick Policy Guideline</h4>
            <p className="text-[12px] text-t3 mt-1 leading-relaxed">
              {cheapestLimitCat
                ? <>Claims under <b className="text-t1">{fmtAmt(cheapestLimitCat.mx)}</b> for <b className="text-t1">{cheapestLimitCat.nm}</b> have the lowest category limit{cheapestLimitCat.rcptAbove ? <> — receipts required above {fmtAmt(cheapestLimitCat.rcptAbove)}</> : '.'}</>
                : "Check each category's limit and receipt requirement before submitting a claim."}
            </p>
          </div>
        </div>
        <div className="bg-bg1 border border-b0 rounded-2xl p-4 shadow-sm flex items-start gap-3">
          <div className="p-2 bg-gb text-g rounded-xl flex-shrink-0"><Calendar size={18} /></div>
          <div>
            <h4 className="text-[13px] font-bold text-t0">Last Disbursement</h4>
            <p className="text-[12px] text-t3 mt-1 leading-relaxed">
              {lastRun
                ? <>Ref <b className="text-t1">{lastRun.ref}</b> — {fmtAmt(lastRun.total)} across {lastRun.count} entr{lastRun.count === 1 ? 'y' : 'ies'}, processed {lastRun.paidAt ? fmtDMY(lastRun.paidAt) : "—"}.</>
                : "No disbursement runs recorded yet."}
            </p>
          </div>
        </div>
        <div className="bg-bg1 border border-b0 rounded-2xl p-4 shadow-sm flex items-start gap-3">
          <div className="p-2 bg-pub text-pu rounded-xl flex-shrink-0"><TrendingUp size={18} /></div>
          <div>
            <h4 className="text-[13px] font-bold text-t0">Top Category This Month</h4>
            <p className="text-[12px] text-t3 mt-1 leading-relaxed">
              {topCatEntry
                ? <><b className="text-t1">{topCatEntry[0]}</b> leads this month at {fmtAmt(topCatEntry[1])}.</>
                : "No claims logged for the current month yet."}
            </p>
          </div>
        </div>
      </div>
    </div>}
    {expTab === "approvals" && <div>
      {expTeam.length === 0
        ? <div className="text-center p-[30px] text-t3 text-[14px] bg-bg1 border border-b0 rounded-2xl">No pending expenses for your centre</div>
        : <div className="rounded-2xl border border-b0 overflow-hidden shadow-card">
            <Tbl bare cols={[
              { k: "e", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.enm} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.enm}</div><div className="text-[12px] text-t3">{r.edpt} · {r.eofc}</div></div></div> },
              { k: "cat", l: "Category", r: r => <Bg c={r.catClr || K.p} sm>{r.catNm || r.type}</Bg> },
              { k: "date", l: "Date", r: r => <span className="text-[14px] text-t1">{r.expDate || "—"}</span> },
              { k: "amt", l: "Amount", r: r => <span className="font-bold">{fmtAmt(r.amt)}</span> },
              { k: "desc", l: "Description", r: r => <span className="text-[12px] text-t2 max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap inline-block">{r.desc}</span> },
              { k: "rcpt", l: "Receipt", r: r => r.rcpt ? <CheckCircle size={11} color={K.g} /> : <XCircle size={11} color={K.r} /> },
              { k: "a", l: "Actions", r: r => <div className="flex gap-[3px]">
                <Bt v="ghost" s="xs" icon={Eye} onClick={() => openExpDet(r.id)} />
                {can('expenses', 'approvals', 'approve_reimbursement', 'approve') && <Bt v="success" s="xs" icon={Check} onClick={() => approveExp(r.id, "approve")} disabled={approvingId === r.id} />}
                {can('expenses', 'approvals', 'reject_reimbursement', 'reject') && <Bt v="danger" s="xs" icon={X} onClick={() => approveExp(r.id, "Rejected")} disabled={approvingId === r.id} />}
              </div> }
            ]} data={approvalsPage.pageData} />
            <Pagination total={expTeam.length} page={approvalsPage.page} pageSize={approvalsPage.pageSize} onPageChange={approvalsPage.onPageChange} onPageSizeChange={approvalsPage.onPageSizeChange} />
          </div>}
    </div>}
    {expTab === "bills" && <div>
      <div className="flex justify-between items-center mb-[10px] flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] text-t3">Company</span>
          <Sl w={180} options={[{ v: "all", l: "All Companies" }, ...refCompanies.map(c => ({ v: String(c.id), l: c.name }))]} value={billsCompany} onChange={setBillsCompany} />
          <span className="text-[14px] text-t3">Location</span>
          <Sl w={170} options={[{ v: "all", l: "All Locations" }, ...refOffices.map(o => ({ v: String(o.id), l: o.name || o.city }))]} value={billsOffice} onChange={setBillsOffice} />
          <span className="text-[14px] text-t3">Status</span>
          <Sl w={170} options={[
            { v: 'all',              l: 'All statuses' },
            { v: 'Pending Approval', l: 'Pending Approval' },
            { v: 'Approved',         l: 'Approved (payable)' },
            { v: 'Paid',             l: 'Paid' },
            { v: 'Rejected',         l: 'Rejected' },
          ]} value={billsStatus} onChange={setBillsStatus} />
          <span className="text-[14px] text-t2">· {bills.length} bill(s)</span>
        </div>
        <div className="flex gap-[6px] flex-wrap">
          <Bt v="secondary" s="sm" onClick={() => setAgeingOpen(true)}>Ageing</Bt>
          <Bt v="secondary" s="sm" onClick={() => setGenRecurringOpen(true)}>Generate Recurring</Bt>
          <Bt s="sm" icon={Plus} onClick={() => setBillModal({})}>Add Bill</Bt>
        </div>
      </div>
      <div className="text-[14px] text-t3 mb-[8px]">
        Direct expenses paid by Accounts to third-party vendors (rent, utilities, professional fees, etc.).
        Flow: Accounts <b className="text-t1">uploads</b> a bill → Center Head <b className="text-t1">approves</b> it → Accounts <b className="text-t1">pays</b> from Payment Processing.
      </div>
      {bills.length === 0
        ? <div className="text-center p-[30px] text-t3 text-[14px] bg-bg1 border border-b0 rounded-2xl">{billsStatus === 'all' ? <>No vendor bills yet. Click <b className="text-t0">Add Bill</b> to record one.</> : `No ${billsStatus} bills.`}</div>
        : <div className="rounded-2xl border border-b0 overflow-hidden shadow-card">
            <Tbl bare cols={[
              { k: "v", l: "Vendor", r: r => <div><div className="text-[14px] font-semibold text-t0 flex items-center gap-[5px]">{r.vendor || "—"} {r.isRecurring && <Bg c={K.tl} sm>Recurring</Bg>}</div><div className="text-[12px] text-t3 font-mono">{r.id}</div></div> },
              { k: "co", l: "Company", r: r => <span className="text-[14px] text-t1">{r.company || "—"}</span> },
              { k: "loc", l: "Location", r: r => <span className="text-[14px] text-t1">{r.office || "—"}</span> },
              { k: "cat", l: "Category", r: r => <Bg c={r.catClr || K.p} sm>{r.catNm || r.cat}</Bg> },
              { k: "bn", l: "Bill No / Period", r: r => <div><div className="text-[14px] text-t0">{r.billNo || "—"}</div><div className="text-[12px] text-t3">{r.periodLabel || ""}</div></div> },
              { k: "bd", l: "Bill Date", r: r => <span className="text-[14px] text-t1">{fmtDDMMYY(r.billDate) || "—"}</span> },
              { k: "tot", l: "Total", r: r => <span className="text-[14px] text-t0">{fmtAmt(r.total)}</span> },
              { k: "tds", l: "TDS", r: r => <span className="text-[14px] text-r">{r.tdsAmt ? '−' + fmtAmt(r.tdsAmt) : '—'}</span> },
              { k: "net", l: "Net Payable", r: r => <span className="font-bold text-[14px]">{fmtAmt(r.netPayable)}</span> },
              { k: "st", l: "Status", r: r => <Bg c={billStatusColor(r.status)} sm dot title={r.status === 'Rejected' && r.rejectionReason ? `Rejected: ${r.rejectionReason}` : undefined}>{r.status}</Bg> },
              { k: "a", l: "", r: r => <div className="flex gap-[3px]">
                <Bt v="ghost" s="xs" icon={Eye} onClick={() => setBillDet({ id: r.id })} />
                {r.invoiceUrl && <a href={uploadUrl(r.invoiceUrl)} target="_blank" rel="noreferrer" title="View invoice" className="bg-transparent border-0 text-bl cursor-pointer p-[2px] flex items-center"><FileText size={12} /></a>}
                {r.status === 'Pending Approval' && canApproveBills && (
                  <Bt v="success" s="xs" icon={Check} disabled={billActionId === r.id} onClick={async () => {
                    if (!(await confirmDialog(`Approve bill ${r.id} — ${r.vendor}? Once approved, Accounts can pay it.`))) return;
                    setBillActionId(r.id);
                    try {
                      await api.patch('/api/vendor-bills/' + r.id + '/approve', {});
                      loadBills(billsCompany);
                      notify('Vendor bill approved', { title: 'Approved', variant: 'success' });
                    } catch (e) { notify(e.message || 'Approve failed', { title: 'Error', variant: 'error' }); }
                    finally { setBillActionId(null); }
                  }}>{billActionId === r.id ? 'Approving…' : 'Approve'}</Bt>
                )}
                {r.status === 'Pending Approval' && canRejectBills && (
                  <Bt v="warning" s="xs" icon={XCircle} onClick={() => setBillReject({ id: r.id, vendor: r.vendor })}>Reject</Bt>
                )}
                {r.status === 'Pending Approval' && <Bt v="ghost" s="xs" icon={Edit} onClick={() => setBillModal(r)} />}
                {(r.status === 'Pending Approval' || r.status === 'Approved' || r.status === 'Rejected') &&
                  <Bt v="ghost" s="xs" icon={X} disabled={billActionId === r.id} onClick={async () => {
                    if (!(await confirmDialog('Cancel bill ' + r.id + '?'))) return;
                    setBillActionId(r.id);
                    try {
                      await api.del('/api/vendor-bills/' + r.id);
                      loadBills(billsCompany);
                      notify('Vendor bill cancelled', { title: 'Saved', variant: 'success' });
                    } catch (e) { notify(e.message || 'Failed to cancel bill', { title: 'Error', variant: 'error' }); }
                    finally { setBillActionId(null); }
                  }} />}
              </div> },
            ]} data={billsPage.pageData} />
            <Pagination total={bills.length} page={billsPage.page} pageSize={billsPage.pageSize} onPageChange={billsPage.onPageChange} onPageSizeChange={billsPage.onPageSizeChange} />
          </div>}
    </div>}
    {expTab === "vendors" && <VendorsManager companies={refCompanies} />}
    {expTab === "payment" && <div>
      <div className="flex justify-between items-center mb-[10px] flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] text-t3">Company</span>
          <Sl w={190} options={refCompanies.map(c => ({ v: String(c.id), l: c.name }))} value={payCompany} onChange={setPayCompany} />
          <span className="text-[14px] text-t2">· {payQueue.length} ready to pay</span>
          {payQueue.length > 0 && (() => { const r = payQueue.filter(x => x.type === 'reimbursement').length; const v = payQueue.length - r; return <span className="text-[12px] text-t3">({r} reimbursement{r === 1 ? '' : 's'}, {v} vendor bill{v === 1 ? '' : 's'})</span>; })()}
        </div>
        <Bt v="success" s="sm" icon={DollarSign} disabled={expSelIds.length === 0} onClick={() => setExpPayModal(true)}>Proceed to Pay ({expSelIds.length})</Bt>
      </div>
      <div className="text-[14px] text-t3 mb-[8px]">One bank file per company per run. Includes <b className="text-t0">approved reimbursements</b> + <b className="text-t0">CH-approved vendor bills</b> for the selected company.</div>
      <div className="rounded-xl border border-b0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full border-collapse text-[14px]"><thead><tr className="bg-bg2">
        <th className="px-2 py-[6px] text-center w-[30px]"><input type="checkbox" checked={expSelIds.length === payQueue.length && payQueue.length > 0} onChange={toggleAllSel} /></th>
        <th className="px-2 py-[6px] text-left text-t3 font-semibold text-[12px] uppercase tracking-[0.07em]">TYPE</th>
        <th className="px-2 py-[6px] text-left text-t3 font-semibold text-[12px] uppercase">BENEFICIARY</th>
        <th className="px-2 py-[6px] text-left text-t3 font-semibold text-[12px] uppercase">CATEGORY</th>
        <th className="px-2 py-[6px] text-left text-t3 font-semibold text-[12px] uppercase">DATE</th>
        <th className="px-2 py-[6px] text-right text-t3 font-semibold text-[12px] uppercase">GROSS</th>
        <th className="px-2 py-[6px] text-right text-t3 font-semibold text-[12px] uppercase">TDS</th>
        <th className="px-2 py-[6px] text-right text-t3 font-semibold text-[12px] uppercase">NET PAYABLE</th>
        <th className="px-2 py-[6px] text-left text-t3 font-semibold text-[12px] uppercase">REFERENCE</th>
      </tr></thead><tbody>{payQueue.map(r => <tr key={r.id} className={`border-b border-b0 hover:bg-sh ${expSelIds.includes(r.id) ? "bg-pg" : ""}`}>
        <td className="px-2 py-[5px] text-center"><input type="checkbox" checked={expSelIds.includes(r.id)} onChange={() => toggleSel(r.id)} /></td>
        <td className="px-2 py-[5px]"><Bg c={r.type === 'vendor' ? K.pu : K.bl} sm>{r.type === 'vendor' ? 'Vendor' : 'Reimb.'}</Bg></td>
        <td className="px-2 py-[5px]"><div className="flex items-center gap-[5px]"><Av nm={r.beneficiary} sz={20} rn={r.type === 'vendor' ? K.pu : K.bl} /><span className="font-semibold text-[14px]">{r.beneficiary}</span></div></td>
        <td className="px-2 py-[5px]"><Bg c={r.catClr || K.p} sm>{r.cat || '—'}</Bg></td>
        <td className="px-2 py-[5px] text-t2">{r.refDate || "—"}</td>
        <td className="px-2 py-[5px] text-right">{fmtAmt(r.gross)}</td>
        <td className="px-2 py-[5px] text-right text-r">{r.tds ? '−' + fmtAmt(r.tds) : '—'}</td>
        <td className="px-2 py-[5px] text-right font-bold">{fmtAmt(r.net)}</td>
        <td className="px-2 py-[5px] text-t2 text-[12px]">{r.reference || '—'}</td>
      </tr>)}</tbody></table></div>
      {expSelIds.length > 0 && <div className="px-3 py-2 bg-bg2 flex justify-between items-center border-t border-b0">
        <span className="text-[14px] text-t0 font-semibold">{expSelIds.length} selected · Net Payable: {fmtAmt(payQueue.filter(e => expSelIds.includes(e.id)).reduce((s, e) => s + (e.net || 0), 0))}</span>
      </div>}
      </div>
      {payQueue.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">Nothing ready to pay for this company. Approved reimbursements and recorded vendor bills show here.</div>}
      {expPayModal && <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => setExpPayModal(false)}>
        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[684px] max-h-[85vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
          <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Confirm Payment</span><button onClick={() => setExpPayModal(false)} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
          <div className="p-3 bg-bg2 rounded-[10px] border border-b0 mb-3">
            <div className="flex justify-between mb-1"><span className="text-[14px] text-t2">Company</span><span className="text-[14px] font-bold text-t0">{refCompanies.find(c => String(c.id) === payCompany)?.name || "—"}</span></div>
            {(() => { const sel = payQueue.filter(x => expSelIds.includes(x.id)); const r = sel.filter(x => x.type === 'reimbursement'); const v = sel.filter(x => x.type === 'vendor'); const gross = sel.reduce((s, x) => s + (x.gross || 0), 0); const tds = sel.reduce((s, x) => s + (x.tds || 0), 0); const net = sel.reduce((s, x) => s + (x.net || 0), 0); return <>
              <div className="flex justify-between mb-1"><span className="text-[14px] text-t2">Reimbursements</span><span className="text-[14px] font-semibold text-t0">{r.length}</span></div>
              <div className="flex justify-between mb-1"><span className="text-[14px] text-t2">Vendor bills</span><span className="text-[14px] font-semibold text-t0">{v.length}</span></div>
              <div className="flex justify-between mb-1 pt-1 border-t border-b0 mt-1"><span className="text-[14px] text-t2">Gross</span><span className="text-[14px] font-semibold text-t0">{fmtAmt(gross)}</span></div>
              <div className="flex justify-between mb-1"><span className="text-[14px] text-t2">TDS deducted</span><span className="text-[14px] font-semibold text-r">−{fmtAmt(tds)}</span></div>
              <div className="flex justify-between"><span className="text-[14px] text-t2">Net Payable</span><span className="text-[15px] font-extrabold text-g">{fmtAmt(net)}</span></div>
            </>; })()}
          </div>
          <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Remarks</div><textarea value={expPayRemarks} onChange={e => setExpPayRemarks(e.target.value)} placeholder="Add payment remarks (optional)..." className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y" /></div>
          <div className="text-[12px] text-t3 mb-3">An Excel bank file will be generated and downloaded after confirmation.</div>
          <div className="flex gap-[6px]"><Bt v="success" full icon={DollarSign} onClick={doBulkPay} disabled={paying}>{paying ? "Processing…" : "Confirm Payment"}</Bt><Bt v="secondary" full onClick={() => setExpPayModal(false)} disabled={paying}>Cancel</Bt></div>
        </div>
      </div>}
    </div>}
    {expTab === "bankfiles" && <div>
      <div className="text-[14px] text-t2 mb-[10px]">All processed payment bank files. Re-download any file for your records or bank upload.</div>
      <Tbl cols={[
        { k: "d", l: "Processed On", r: r => <span className="text-[14px] text-t1">{r.paidAt ? fmtDMY(r.paidAt) : "—"}</span> },
        { k: "ref", l: "Reference", r: r => <span className="font-mono text-[14px] font-semibold text-t0">{r.ref}</span> },
        { k: "co", l: "Company", r: r => <span className="text-[14px] text-t1">{r.company || "—"}</span> },
        { k: "cnt", l: "Entries", r: r => <span className="text-[14px]">{r.count}</span> },
        { k: "tot", l: "Total", r: r => <span className="font-bold">{fmtAmt(r.total)}</span> },
        { k: "by", l: "Processed By", r: r => <span className="text-[14px] text-t2">{r.payer || "—"}</span> },
        { k: "a", l: "", r: r => <Bt v="ghost" s="xs" icon={FileSpreadsheet} onClick={() => downloadFile('/api/expenses/bank-file/' + r.ref, `expense-bankfile-${r.ref}.xlsx`)}>Excel</Bt> },
      ]} data={payRuns} />
      {payRuns.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No bank files generated yet</div>}
    </div>}
    {expTab === "completed" && <div>
      <div className="flex flex-wrap gap-2 mb-[10px] items-end">
        <div><div className="text-[12px] text-t3 mb-[2px]">Paid from</div><Inp type="date" value={cFilters.from} onChange={v => setCFilters(p => ({ ...p, from: v }))} style={{ width: 140 }} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Paid to</div><Inp type="date" value={cFilters.to} onChange={v => setCFilters(p => ({ ...p, to: v }))} style={{ width: 140 }} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Company</div><Sl w={160} options={[{ v: "all", l: "All Companies" }, ...refCompanies.map(c => ({ v: String(c.id), l: c.name }))]} value={cFilters.company} onChange={v => setCFilters(p => ({ ...p, company: v }))} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Location</div><Sl w={160} options={[{ v: "all", l: "All Locations" }, ...refOffices.map(o => ({ v: String(o.id), l: o.name || o.city }))]} value={cFilters.office} onChange={v => setCFilters(p => ({ ...p, office: v }))} /></div>
        <Bt s="sm" onClick={() => loadCompleted()}>Apply</Bt>
        <Bt v="ghost" s="sm" onClick={() => { const r = { from: "", to: "", company: "all", office: "all" }; setCFilters(r); loadCompleted(r); }}>Reset</Bt>
      </div>
      <div className="mb-2 text-[14px] text-t2">{completedList.length} payment(s) · Total {fmtAmt(completedList.reduce((s, e) => s + (e.amt || 0), 0))}</div>
      {completedList.length === 0
        ? <div className="text-center p-[30px] text-t3 text-[14px] bg-bg1 border border-b0 rounded-2xl">No completed payments for the selected filters</div>
        : <div className="rounded-2xl border border-b0 overflow-hidden shadow-card">
            <Tbl bare cols={[
              { k: "pd", l: "Paid On", r: r => <span className="text-[14px] text-t1">{r.paidAt ? fmtDMY(r.paidAt) : "—"}</span> },
              { k: "ty", l: "Type", r: r => <Bg c={r.type === 'vendor' ? K.pu : K.bl} sm>{r.type === 'vendor' ? 'Vendor' : 'Reimb.'}</Bg> },
              { k: "e", l: "Beneficiary", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.beneficiary} sz={20} rn={r.type === 'vendor' ? K.pu : K.bl} /><div><div className="text-[15px] font-bold text-t0">{r.beneficiary}</div><div className="text-[12px] text-t3">{r.location || ""}</div></div></div> },
              { k: "cat", l: "Category", r: r => <Bg c={r.catClr || K.p} sm>{r.cat || '—'}</Bg> },
              { k: "amt", l: "Amount", r: r => <span className="font-bold">{fmtAmt(r.amt)}</span> },
              { k: "ref", l: "Bank Ref", r: r => <span className="font-mono text-[12px] text-t2">{r.payRef || "—"}</span> },
              { k: "a", l: "", r: r => r.type === 'reimbursement' ? <Bt v="ghost" s="xs" icon={Eye} onClick={() => openExpDet(r.id)}>View</Bt> : null },
            ]} data={completedPage.pageData} />
            <Pagination total={completedList.length} page={completedPage.page} pageSize={completedPage.pageSize} onPageChange={completedPage.onPageChange} onPageSizeChange={completedPage.onPageSizeChange} />
          </div>}
    </div>}
    {expTab === "ledger" && <OfficeExpenses expCats={expCats} can={can} />}
    {expApply && <SubmitExpModal />}
    {expReport && <ExpReportModal />}
    {billModal && <BillModal cur={billModal} vendors={vendors} vendorCats={vendorCats} companies={refCompanies} offices={refOffices} onReloadVendors={() => loadVendors(true)} onClose={() => setBillModal(null)} onSaved={() => { setBillModal(null); loadBills(billsCompany); }} />}
    {billReject && <BillRejectModal bill={billReject} onClose={() => setBillReject(null)} onDone={() => { setBillReject(null); loadBills(billsCompany); }} />}
    {genRecurringOpen && <GenRecurringModal
      companies={refCompanies}
      defaultCompany={billsCompany !== 'all' ? billsCompany : ''}
      onClose={() => setGenRecurringOpen(false)}
      onDone={() => { setGenRecurringOpen(false); loadBills(billsCompany); }}
    />}
    {ageingOpen && <AgeingModal company={billsCompany} onClose={() => setAgeingOpen(false)} />}
    {billDet && <BillDetailModal id={billDet.id} onClose={() => setBillDet(null)} />}
  </div>;
};

// ── Reject Bill (Center Head only) ──
// Rejection reason is required so Accounts knows what to fix. Optimistic
// close after a successful PATCH; on error we stay open and surface the
// server message.
const BillRejectModal = ({ bill, onClose, onDone }) => {
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const submit = async () => {
    if (!reason.trim()) { setErr('A reason is required so Accounts knows what to fix.'); return; }
    setSaving(true); setErr('');
    try {
      await api.patch(`/api/vendor-bills/${bill.id}/reject`, { reason: reason.trim() });
      onDone();
      notify('Vendor bill rejected', { title: 'Rejected', variant: 'success' });
    } catch (e) { setErr(e.message || 'Reject failed'); notify(e.message || 'Failed to reject bill', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 w-full max-w-[628px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-2">
          <span className="text-[16px] font-extrabold text-t0">Reject Vendor Bill</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={16} /></button>
        </div>
        <div className="text-[14px] text-t2 mb-3">Rejecting <b className="text-t0">{bill.id}</b>{bill.vendor ? ` — ${bill.vendor}` : ''}. Accounts will see the reason below and can upload a corrected bill.</div>
        <div className="text-[14px] text-t3 mb-1">Reason <span className="text-r">*</span></div>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          className="w-full min-h-[80px] px-2 py-1 bg-bg2 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none resize-y"
          placeholder="Missing GSTIN, wrong TDS section, amount off by ₹2000, …"
        />
        {err && <div className="text-[14px] text-r mt-2">{err}</div>}
        <div className="flex gap-2 mt-3">
          <Bt v="warning" s="sm" icon={XCircle} onClick={submit} disabled={saving} full>{saving ? 'Rejecting…' : 'Reject Bill'}</Bt>
          <Bt v="secondary" s="sm" onClick={onClose} full>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// ── Add / Edit Vendor Bill ──
// Self-contained modal: vendor dropdown autofills bank/GSTIN/default TDS;
// GST + TDS + Net Payable are computed live; invoice file optional.
const BillModal = ({ cur, vendors, vendorCats, companies, offices, onReloadVendors, onClose, onSaved }) => {
  const isEdit = !!cur?.id;
  const init = isEdit ? {
    vendor_id: String(cur.vendorId || ''),
    company_id: String(cur.companyId || ''),
    office_id: String(cur.officeId || ''),
    category_code: cur.cat || '',
    bill_no: cur.billNo || '',
    bill_date: cur.billDate || new Date().toISOString().slice(0, 10),
    due_date: cur.dueDate || '',
    period_label: cur.periodLabel || '',
    description: cur.desc || '',
    taxable_amount: String(cur.taxable || ''),
    cgst: String(cur.cgst || '0'),
    sgst: String(cur.sgst || '0'),
    igst: String(cur.igst || '0'),
    tds_section: cur.tdsSection || '',
    tds_rate: String(cur.tdsRate || '0'),
    remarks: cur.remarks || '',
    is_recurring: !!cur.isRecurring,
  } : {
    vendor_id: '', company_id: '', office_id: '', category_code: '',
    bill_no: '', bill_date: new Date().toISOString().slice(0, 10), due_date: '', period_label: '',
    description: '',
    taxable_amount: '', cgst: '0', sgst: '0', igst: '0',
    tds_section: '', tds_rate: '0', remarks: '',
    is_recurring: false,
  };
  const [f, setF] = React.useState(init);
  const [file, setFile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showNewVendor, setShowNewVendor] = React.useState(false);

  const sel = vendors.find(v => String(v.id) === f.vendor_id);
  const clearFieldError = (k) => setFieldErrors(p => (p[k] ? { ...p, [k]: undefined } : p));

  const onVendorChange = (vid) => {
    const v = vendors.find(x => String(x.id) === vid);
    setF(p => ({
      ...p,
      vendor_id: vid,
      company_id: v && v.companyId ? String(v.companyId) : p.company_id,
      category_code: p.category_code || (v?.defaultCategory) || '',
      tds_section: p.tds_section || (v?.defaultTdsSection) || '',
      tds_rate: (p.tds_rate && p.tds_rate !== '0') ? p.tds_rate : (v ? String(v.defaultTdsRate || 0) : '0'),
    }));
    clearFieldError('vendor_id');
  };
  // Each section's statutory rate, used when one is picked and no rate has
  // been set yet.
  const TDS_SECTION_RATES = { '194C': 2, '194I': 10, '194J': 10, '194H': 5 };
  const onTdsSectionChange = (sec) => {
    setF(p => ({
      ...p,
      tds_section: sec,
      // None means no deduction — clearing the section has to clear the
      // rate a vendor/category default filled in, or the bill silently
      // deducts at that rate with no section against it.
      tds_rate: !sec ? '0'
        : (p.tds_rate && p.tds_rate !== '0') ? p.tds_rate
        : String(TDS_SECTION_RATES[sec] || 0),
    }));
  };
  const onCategoryChange = (cd) => {
    const c = vendorCats.find(x => x.cd === cd);
    setF(p => ({
      ...p,
      category_code: cd,
      tds_section: p.tds_section || (c?.sec) || '',
      tds_rate: (p.tds_rate && p.tds_rate !== '0') ? p.tds_rate : (c ? String(c.rate || 0) : '0'),
    }));
  };

  // Live totals
  const taxable = parseFloat(f.taxable_amount) || 0;
  const cgst = parseFloat(f.cgst) || 0;
  const sgst = parseFloat(f.sgst) || 0;
  const igst = parseFloat(f.igst) || 0;
  const total = taxable + cgst + sgst + igst;
  const tdsRate = f.tds_section ? (parseFloat(f.tds_rate) || 0) : 0;
  const tdsAmt = Math.round((taxable * tdsRate)) / 100;
  const netPayable = Math.max(total - tdsAmt, 0);
  const fmt = v => `₹${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const save = async () => {
    const fe = {};
    if (!f.vendor_id) fe.vendor_id = 'Pick a vendor';
    if (!f.company_id) fe.company_id = 'Pick a company';
    if (!f.bill_date) fe.bill_date = 'Bill date is required';
    if (!(taxable > 0)) fe.taxable_amount = 'Enter an amount greater than 0';
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;
    setSaving(true); setErr('');
    try {
      if (isEdit) {
        await api.put('/api/vendor-bills/' + cur.id, {
          category_code: f.category_code || null, bill_no: f.bill_no || null,
          bill_date: f.bill_date, due_date: f.due_date || null, period_label: f.period_label || null,
          description: f.description || null,
          taxable_amount: taxable, cgst, sgst, igst,
          tds_section: f.tds_section || null, tds_rate: tdsRate,
          remarks: f.remarks || null,
          is_recurring: !!f.is_recurring,
          office_id: f.office_id || null,
        });
      } else {
        const fd = new FormData();
        Object.entries({
          vendor_id: f.vendor_id, company_id: f.company_id, office_id: f.office_id,
          category_code: f.category_code, bill_no: f.bill_no, bill_date: f.bill_date,
          due_date: f.due_date, period_label: f.period_label, description: f.description,
          taxable_amount: String(taxable), cgst: String(cgst), sgst: String(sgst), igst: String(igst),
          tds_section: f.tds_section, tds_rate: String(tdsRate), remarks: f.remarks,
          is_recurring: f.is_recurring ? 'true' : 'false',
        }).forEach(([k, v]) => fd.append(k, v ?? ''));
        if (file) fd.append('invoice', file);
        await api.upload('/api/vendor-bills', fd);
      }
      onSaved();
      notify(isEdit ? 'Vendor bill updated' : 'Vendor bill recorded', { title: 'Saved', variant: 'success' });
    } catch (e) { setErr(e.message || 'Save failed'); notify(e.message || 'Failed to save vendor bill', { title: 'Error', variant: 'error' }); } finally { setSaving(false); }
  };

  return (
    <>
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[1047px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <span className="text-[18px] font-extrabold text-t0">{isEdit ? 'Edit Vendor Bill' : 'Add Vendor Bill'}</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-3">
          <div><div className="text-[14px] text-t2 mb-[3px] flex items-center justify-between"><span>Vendor <span className="text-r">*</span></span><button type="button" onClick={() => setShowNewVendor(true)} className="text-[12px] font-semibold text-p hover:underline bg-transparent border-0 cursor-pointer p-0">+ New vendor</button></div>
            <SearchSelect
              options={vendors.map(v => ({ v: String(v.id), l: v.name, sub: [v.gstin, v.state, v.bankName].filter(Boolean).join(' · ') }))}
              value={f.vendor_id}
              onChange={onVendorChange}
              placeholder="— Select vendor —"
              searchPlaceholder="Search by name, GSTIN or state…"
              emptyText="No vendors match. Use “+ New vendor”."
              error={!!fieldErrors.vendor_id}
            />
            {fieldErrors.vendor_id ? <div className="text-[12px] text-r mt-1">{fieldErrors.vendor_id}</div>
              : sel && <div className="text-[12px] text-t3 mt-1">{sel.bankName ? sel.bankName + ' ' + (sel.account || '') : 'No bank details on file'} · {sel.state || '—'}</div>}
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Company <span className="text-r">*</span></div>
            <Sl options={[{ v: '', l: '— Select company —' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={f.company_id} onChange={v => { setF(p => ({ ...p, company_id: v })); clearFieldError('company_id'); }} error={!!fieldErrors.company_id} />
            {fieldErrors.company_id && <div className="text-[12px] text-r mt-1">{fieldErrors.company_id}</div>}
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Centre</div>
            <Sl options={[{ v: '', l: '— Select centre —' }, ...offices.map(o => ({ v: String(o.id), l: o.name || o.city }))]} value={f.office_id} onChange={v => setF(p => ({ ...p, office_id: v }))} />
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Category</div>
            <Sl options={[{ v: '', l: '— Select category —' }, ...vendorCats.filter(c => c.active).map(c => ({ v: c.cd, l: c.nm }))]} value={f.category_code} onChange={onCategoryChange} />
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Period</div>
            <Inp placeholder="e.g. Apr 2026" value={f.period_label} onChange={v => setF(p => ({ ...p, period_label: v }))} />
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Bill No.</div>
            <Inp placeholder="e.g. INV-2026-001" value={f.bill_no} onChange={v => setF(p => ({ ...p, bill_no: v }))} />
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Bill Date <span className="text-r">*</span></div>
            <Inp type="date" value={f.bill_date} onChange={v => { setF(p => ({ ...p, bill_date: v })); clearFieldError('bill_date'); }} error={!!fieldErrors.bill_date} />
            {fieldErrors.bill_date && <div className="text-[12px] text-r mt-1">{fieldErrors.bill_date}</div>}
          </div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Due Date</div>
            <Inp type="date" value={f.due_date} onChange={v => setF(p => ({ ...p, due_date: v }))} />
          </div>
          <div className="sm:col-span-2"><div className="text-[14px] text-t2 mb-[3px]">Description</div>
            <Inp placeholder="What this bill is for…" value={f.description} onChange={v => setF(p => ({ ...p, description: v }))} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-[10px] mb-3">
          <div><div className="text-[14px] text-t2 mb-[3px]">Invoice Value (₹) <span className="text-r">*</span></div><Inp type="number" value={f.taxable_amount} onChange={v => { setF(p => ({ ...p, taxable_amount: v })); clearFieldError('taxable_amount'); }} error={!!fieldErrors.taxable_amount} />{fieldErrors.taxable_amount && <div className="text-[12px] text-r mt-1">{fieldErrors.taxable_amount}</div>}</div>
          <div><div className="text-[14px] text-t2 mb-[3px]">CGST (₹)</div><Inp type="number" value={f.cgst} onChange={v => setF(p => ({ ...p, cgst: v, sgst: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">SGST (₹)</div><Inp type="number" value={f.sgst} onChange={v => setF(p => ({ ...p, sgst: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">IGST (₹)</div><Inp type="number" value={f.igst} onChange={v => setF(p => ({ ...p, igst: v }))} /></div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-[10px] mb-3">
          <div><div className="text-[14px] text-t2 mb-[3px]">TDS Section</div>
            <Sl options={[{ v: '', l: 'None' }, { v: '194C', l: '194C — Contractor' }, { v: '194I', l: '194I — Rent' }, { v: '194J', l: '194J — Professional' }, { v: '194H', l: '194H — Commission' }]} value={f.tds_section} onChange={onTdsSectionChange} />
          </div>
          <div>
            <div className="text-[14px] text-t2 mb-[3px]">TDS Rate (%)</div>
            <Inp type="number" value={f.tds_section ? f.tds_rate : '0'} onChange={v => setF(p => ({ ...p, tds_rate: v }))} disabled={!f.tds_section} />
            {!f.tds_section && <div className="text-[12px] text-t3 mt-[2px]">No section selected — nothing is deducted.</div>}
          </div>
          <div className="col-span-2 sm:col-span-2">
            <div className="text-[14px] text-t2 mb-[3px]">Invoice (PDF / image)</div>
            <label className="flex items-center gap-2 px-3 py-2 bg-bg2 border border-dashed border-b1 rounded-[10px] cursor-pointer hover:border-p">
              <Upload size={13} className="text-t3 flex-shrink-0" />
              <span className="text-[14px] text-t2 truncate">{file ? file.name : (cur?.invoiceUrl ? 'Already attached (change?)' : 'Attach tax invoice')}</span>
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={e => setFile(e.target.files[0] || null)} />
            </label>
          </div>
        </div>

        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={!!f.is_recurring} onChange={e => setF(p => ({ ...p, is_recurring: e.target.checked }))} className="mt-[3px] flex-shrink-0" />
          <div>
            <div className="text-[14px] font-semibold text-t0">Mark as recurring</div>
            <div className="text-[12px] text-t3">Use this bill as a monthly template. Use the <b className="text-t1">Generate Recurring Bills</b> button each month to clone it (same amounts, blank invoice) for that period.</div>
          </div>
        </label>

        <div className="p-3 bg-bg2 rounded-xl border border-b0 mb-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div><div className="text-[12px] text-t3 uppercase tracking-wide">Total (incl. GST)</div><div className="text-[15px] font-bold text-t0">{fmt(total)}</div></div>
            <div><div className="text-[12px] text-t3 uppercase tracking-wide">TDS Deducted</div><div className="text-[15px] font-bold text-r">−{fmt(tdsAmt)}</div></div>
            <div><div className="text-[12px] text-t3 uppercase tracking-wide">Net Payable</div><div className="text-[17px] font-extrabold text-g">{fmt(netPayable)}</div></div>
            <div><div className="text-[12px] text-t3 uppercase tracking-wide">Goes To</div><div className="text-[14px] font-semibold text-t1">Payment Queue</div></div>
          </div>
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-[6px]">
          <Bt full onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Record Bill')}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
    {showNewVendor && <VendorModal
      cur={null}
      vendorCats={vendorCats}
      companies={companies}
      onClose={() => setShowNewVendor(false)}
      onSaved={async (newId) => {
        setShowNewVendor(false);
        if (onReloadVendors) await onReloadVendors();
        if (newId) setF(p => ({ ...p, vendor_id: String(newId) }));
      }}
    />}
    </>
  );
};

// ── Vendor Ageing modal — outstanding balances bucketed by days-overdue ──
const AgeingModal = ({ company, onClose }) => {
  const [data, setData] = React.useState(null);
  const [scope, setScope] = React.useState(company);
  const fmt = (v) => '₹' + (v || 0).toLocaleString('en-IN');
  React.useEffect(() => {
    const qs = scope && scope !== 'all' ? '?company=' + scope : '';
    api.get('/api/vendor-bills/ageing' + qs).then(d => setData(d || { rows: [], totals: {} })).catch(() => setData({ rows: [], totals: {} }));
  }, [scope]);
  const t = data?.totals || {};
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[1244px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-3 items-center"><span className="text-base font-extrabold text-t0">Vendor Ageing — Outstanding Bills</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
        <div className="text-[14px] text-t3 mb-3">Bills <b className="text-t1">recorded but not paid</b>, bucketed by days past their due date.</div>
        {!data ? <div className="text-t3 text-[14px]">Loading…</div> : <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {[
              { l: 'Total Outstanding', v: fmt(t.total), c: K.t0, sub: `${t.vendorCount || 0} vendor(s) · ${t.billCount || 0} bill(s)` },
              { l: '0–30 days', v: fmt(t.b030), c: K.g },
              { l: '31–60 days', v: fmt(t.b3160), c: K.a },
              { l: '61–90 days', v: fmt(t.b6190), c: '#FF8800' },
              { l: '90+ days', v: fmt(t.b90), c: K.r },
            ].map((k, i) => <div key={i} className="bg-bg2 border border-b0 rounded-lg p-2">
              <div className="text-[12px] text-t3 uppercase tracking-wide">{k.l}</div>
              <div className="text-[15px] font-extrabold mt-[2px]" style={{ color: k.c }}>{k.v}</div>
              {k.sub && <div className="text-[12px] text-t3 mt-[2px]">{k.sub}</div>}
            </div>)}
          </div>
          {data.rows.length === 0
            ? <div className="text-center p-[30px] text-t3 text-[14px]">No outstanding vendor bills for this company.</div>
            : <div className="rounded-xl border border-b0 overflow-hidden"><div className="overflow-x-auto">
                <table className="w-full border-collapse text-[14px]">
                  <thead><tr className="bg-bg2">
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Vendor</th>
                    <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Bills</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">Total</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">0–30</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">31–60</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">61–90</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">90+</th>
                  </tr></thead>
                  <tbody>
                    {data.rows.map(r => <tr key={r.vendorId} className="border-b border-b0 hover:bg-sh">
                      <td className="px-2 py-[6px]"><div className="text-[14px] font-semibold text-t0">{r.vendor}</div>{r.gstin && <div className="text-[12px] text-t3 font-mono">{r.gstin}</div>}</td>
                      <td className="px-2 py-[6px] text-center text-[14px]">{r.billCount}</td>
                      <td className="px-2 py-[6px] text-right text-[14px] font-bold text-t0">{fmt(r.total)}</td>
                      <td className="px-2 py-[6px] text-right text-[14px]" style={{ color: r.b030 > 0 ? K.g : K.t3 }}>{fmt(r.b030)}</td>
                      <td className="px-2 py-[6px] text-right text-[14px]" style={{ color: r.b3160 > 0 ? K.a : K.t3 }}>{fmt(r.b3160)}</td>
                      <td className="px-2 py-[6px] text-right text-[14px]" style={{ color: r.b6190 > 0 ? '#FF8800' : K.t3 }}>{fmt(r.b6190)}</td>
                      <td className="px-2 py-[6px] text-right text-[14px] font-semibold" style={{ color: r.b90 > 0 ? K.r : K.t3 }}>{fmt(r.b90)}</td>
                    </tr>)}
                  </tbody>
                </table>
              </div></div>}
        </>}
      </div>
    </div>
  );
};

// ── Vendor Bill detail modal — read-only view with timeline ──
const BillDetailModal = ({ id, onClose }) => {
  const [b, setB] = React.useState(null);
  const fmt = (v) => '₹' + (v || 0).toLocaleString('en-IN');
  React.useEffect(() => {
    api.get('/api/vendor-bills/' + id).then(d => setB(d)).catch(() => setB({ error: 'Failed to load' }));
  }, [id]);
  if (!b) {
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 w-full max-w-[628px]" onMouseDown={e => e.stopPropagation()}><div className="text-t3 text-[14px]">Loading…</div></div>
    </div>;
  }
  if (b.error) {
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 w-full max-w-[628px]" onMouseDown={e => e.stopPropagation()}><div className="text-r text-[14px]">{b.error}</div></div>
    </div>;
  }
  const R = ({ k, v }) => v == null || v === '' ? null : <div className="flex justify-between py-[5px] border-b border-b0 last:border-0"><span className="text-[14px] text-t3">{k}</span><span className="text-[14px] font-semibold text-t1 text-right break-all max-w-[60%]">{v}</span></div>;
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[824px] max-h-[92vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="p-4 border-b border-b0 flex items-start justify-between gap-2">
          <div>
            <div className="text-[17px] font-extrabold text-t0">{b.vendor || '—'}</div>
            <div className="text-[14px] text-t3 font-mono mt-[2px]">{b.id} {b.billNo ? `· ${b.billNo}` : ''}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Bg c={b.status === 'Paid' ? K.g : b.status === 'Approved' ? K.bl : b.status === 'Pending Approval' ? K.a : b.status === 'Rejected' ? K.r : K.t3} sm dot>{b.status}</Bg>
              {b.isRecurring && <Bg c={K.tl} sm>Recurring</Bg>}
              {b.templateId && <span className="text-[12px] text-t3">cloned from {b.templateId}</span>}
            </div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <div>
            <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-1">Bill</div>
            <R k="Category" v={b.catNm} />
            <R k="Company" v={b.company} />
            <R k="Bill Date" v={fmtDDMMYY(b.billDate)} />
            <R k="Due Date" v={fmtDDMMYY(b.dueDate)} />
            <R k="Period" v={b.periodLabel} />
            <R k="Description" v={b.desc} />
          </div>
          <div>
            <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-1">Amounts</div>
            <R k="Taxable" v={fmt(b.taxable)} />
            {b.cgst > 0 && <R k="CGST" v={fmt(b.cgst)} />}
            {b.sgst > 0 && <R k="SGST" v={fmt(b.sgst)} />}
            {b.igst > 0 && <R k="IGST" v={fmt(b.igst)} />}
            <R k="Total" v={fmt(b.total)} />
            {b.tdsAmt > 0 && <R k={`TDS ${b.tdsSection ? `(${b.tdsSection} @ ${b.tdsRate}%)` : ''}`} v={`− ${fmt(b.tdsAmt)}`} />}
            <div className="flex justify-between py-[7px] border-t border-b0 mt-1"><span className="text-[14px] font-bold text-t0">Net Payable</span><span className="text-[15px] font-extrabold text-g">{fmt(b.netPayable)}</span></div>
          </div>
        </div>
        <div className="p-4 border-t border-b0">
          <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Timeline</div>
          <div className="flex items-center gap-2 mb-[6px]"><div className="w-[6px] h-[6px] rounded-full bg-t3" /><span className="text-[14px] text-t1"><b className="text-t0">Uploaded</b> by {b.createdBy || '—'}{b.createdAt ? ` · ${fmtDMY(b.createdAt)}, ${new Date(b.createdAt).toLocaleTimeString('en-IN')}` : ''}</span></div>
          {b.approvedAt && <div className="flex items-center gap-2 mb-[6px]"><div className="w-[6px] h-[6px] rounded-full bg-bl" /><span className="text-[14px] text-t1"><b className="text-t0">Approved</b> by {b.approvedByName || b.approvedBy || '—'} · {fmtDMY(b.approvedAt)}, {new Date(b.approvedAt).toLocaleTimeString('en-IN')}</span></div>}
          {b.status === 'Rejected' && b.rejectionReason && <div className="flex items-start gap-2 mb-[6px]"><div className="w-[6px] h-[6px] rounded-full bg-r mt-[6px]" /><span className="text-[14px] text-t1"><b className="text-r">Rejected</b> — {b.rejectionReason}</span></div>}
          {b.status === 'Paid' && <div className="flex items-center gap-2 mb-[6px]"><div className="w-[6px] h-[6px] rounded-full bg-g" /><span className="text-[14px] text-t1"><b className="text-t0">Paid</b> by {b.paidBy || '—'}{b.paidAt ? ` · ${fmtDMY(b.paidAt)}, ${new Date(b.paidAt).toLocaleTimeString('en-IN')}` : ''}{b.payRef ? ` · ref ${b.payRef}` : ''}</span></div>}
          {b.payRemarks && <div className="text-[14px] text-t2 ml-3 italic">"{b.payRemarks}"</div>}
        </div>
        {b.invoiceUrl && <div className="px-4 pb-4">
          <a href={uploadUrl(b.invoiceUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[14px] font-semibold text-bl hover:underline"><FileText size={12} /> View invoice</a>
        </div>}
      </div>
    </div>
  );
};

// ── Generate Recurring Bills modal ──
// Pick a company + month → clones latest is_recurring=TRUE bills for that
// company into the new period (idempotent, skips duplicates).
const GenRecurringModal = ({ companies, defaultCompany, onClose, onDone }) => {
  const [period, setPeriod] = React.useState(new Date().toISOString().slice(0, 7));
  const [cid, setCid] = React.useState(defaultCompany || (companies[0] ? String(companies[0].id) : ''));
  const [saving, setSaving] = React.useState(false); const [err, setErr] = React.useState('');
  const run = async () => {
    if (!cid) return setErr('Pick a company');
    if (!period) return setErr('Pick a month');
    setSaving(true); setErr('');
    try {
      const r = await api.post('/api/vendor-bills/generate-recurring', { period, company_id: parseInt(cid) });
      notify(`Generated ${r.created} recurring bill(s)${r.skipped ? `, ${r.skipped} already existed` : ''} for ${r.periodLabel}.`, { title: 'Generated', variant: 'success' });
      onDone();
    } catch (e) { setErr(e.message || 'Failed'); notify(e.message || 'Failed to generate recurring bills', { title: 'Error', variant: 'error' }); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[600px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Generate Recurring Bills</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
        <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Company <span className="text-r">*</span></div><Sl options={[{ v: '', l: '— Select —' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={cid} onChange={setCid} /></div>
        <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Month <span className="text-r">*</span></div><Inp type="month" value={period} onChange={setPeriod} /></div>
        <div className="text-[14px] text-t2 bg-bg2 border border-b0 rounded-lg px-3 py-2 mb-3">Clones the <b className="text-t0">most recent recurring bill</b> for each vendor in this company into the chosen month, keeping the same amounts. Bill no &amp; invoice are blank — edit before paying.</div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-[6px]"><Bt full onClick={run} disabled={saving}>{saving ? 'Generating…' : 'Generate'}</Bt><Bt v="secondary" full onClick={onClose}>Cancel</Bt></div>
      </div>
    </div>
  );
};
